/**
 * What goes out of a state and what comes into it - issue #50.
 *
 * Every edge touching the state, WHEREVER it is declared. The list a state's
 * own body declares is the file's shape, and for a leaf it is empty: the edges
 * out of Standby live in the machine around it. So the panel reads what the
 * server reports as `outgoing` and `incoming`, and each row is changed or
 * removed on its own - by its declaring state, its index there and its ends as
 * written, since a transition has no identity (decision 008).
 *
 * A machine's own edges between its states are listed too, read-only: they are
 * each some state's way in or out, and are edited from there.
 */

import { useState } from 'react'
import type { ReactNode } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { EntityRow, StateRow, TouchingTransition, Transition } from '../../schema'
import { useCanWrite } from '../../state/AppData'
import TransitionDialog from './TransitionDialog'
import { stateName } from './state-authoring'

export default function StateTransitions({
  row,
  states,
  onSaved,
}: {
  row: StateRow
  states: EntityRow[]
  onSaved: () => void
}) {
  const { allowed, reason } = useCanWrite()
  const [dialog, setDialog] = useState<{ editing?: TouchingTransition } | null>(null)
  const [failure, setFailure] = useState('')
  const byId = new Map(states.map((state) => [state.id, state]))
  const nameOf = (id: string) => stateName(byId.get(id), id)

  const remove = (edge: TouchingTransition) => {
    setFailure('')
    api.removeTransition(edge).then(onSaved).catch((error: ApiError) => setFailure(error.message))
  }

  const actions = (edge: TouchingTransition, label: string) => (
    <TableCell padding="none" sx={{ whiteSpace: 'nowrap' }}>
      <Tooltip title={allowed ? '' : reason} describeChild>
        <span>
          <IconButton size="small" aria-label={`Change ${label}`} disabled={!allowed}
            onClick={() => setDialog({ editing: edge })}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label={`Remove ${label}`} disabled={!allowed}
            onClick={() => remove(edge)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </TableCell>
  )

  return (
    <Stack spacing={1.5}>
      {failure && <Alert severity="error">{failure}</Alert>}
      <Touching
        title="Out of this state"
        heading="To"
        edges={row.outgoing ?? []}
        far={(edge) => nameOf(edge.to)}
        actions={(edge) => actions(edge, `transition to ${nameOf(edge.to)}`)}
      />
      <Touching
        title="Into this state"
        heading="From"
        edges={row.incoming ?? []}
        far={(edge) => nameOf(edge.from)}
        actions={(edge) => actions(edge, `transition from ${nameOf(edge.from)}`)}
      />
      <Box>
        <Tooltip title={allowed ? '' : reason} describeChild>
          <span>
            <Button size="small" startIcon={<AddIcon />} disabled={!allowed}
              onClick={() => setDialog({})}>
              Add transition
            </Button>
          </span>
        </Tooltip>
      </Box>

      {row.transitions.length > 0 && (
        <Declared transitions={row.transitions} nameOf={nameOf} />
      )}

      {dialog && (
        <TransitionDialog
          state={row}
          states={states}
          editing={dialog.editing}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null)
            onSaved()
          }}
        />
      )}
    </Stack>
  )
}

function Touching({
  title,
  heading,
  edges,
  far,
  actions,
}: {
  title: string
  heading: string
  edges: TouchingTransition[]
  far: (edge: TouchingTransition) => string
  actions: (edge: TouchingTransition) => ReactNode
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      {edges.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None.
        </Typography>
      ) : (
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>{heading}</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Guard</TableCell>
              <TableCell>Effect</TableCell>
              <TableCell>Decided by</TableCell>
              <TableCell>Declared in</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {edges.map((edge) => (
              <TableRow key={`${edge.owner}-${edge.index}`}>
                <TableCell>{far(edge)}</TableCell>
                <TableCell>{edge.trigger || '-'}</TableCell>
                <TableCell>{edge.guard || '-'}</TableCell>
                <TableCell>{edge.effect || '-'}</TableCell>
                <TableCell>{edge.decided_by || '-'}</TableCell>
                <TableCell>{edge.owner_name || edge.owner}</TableCell>
                {actions(edge)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

/** What this state's own body declares: its sub-states' edges, read-only here. */
function Declared({ transitions, nameOf }: { transitions: Transition[]; nameOf: (id: string) => string }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        Declared in this state
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        The edges between the states inside it. Each is changed from the state it leaves or enters.
      </Typography>
      <Table size="small" aria-label="Declared in this state">
        <TableHead>
          <TableRow>
            <TableCell>From</TableCell>
            <TableCell>To</TableCell>
            <TableCell>Trigger</TableCell>
            <TableCell>Guard</TableCell>
            <TableCell>Effect</TableCell>
            <TableCell>Decided by</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transitions.map((edge, index) => (
            <TableRow key={`${edge.from}-${edge.to}-${index}`}>
              <TableCell>{nameOf(edge.from)}</TableCell>
              <TableCell>{nameOf(edge.to)}</TableCell>
              <TableCell>{edge.trigger || '-'}</TableCell>
              <TableCell>{edge.guard || '-'}</TableCell>
              <TableCell>{edge.effect || '-'}</TableCell>
              <TableCell>{edge.decided_by || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}
